import React, { useState } from 'react'
import { useParams } from 'react-router-dom'
import { useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { useMediaQuery, Typography, Box, Stack, Divider, Theme, Chip } from '@mui/material'
import { AddPlatformServices } from '../components/AddPlatformServices'
import { selectCanRegister } from '../selectors/organizations'
import { AddPlatformTags } from '../components/AddPlatformTags'
import { AddDownload } from '../components/AddDownload'
import { AddDevice } from '../components/AddDevice'
import { platforms } from '../platforms'
import { Notice } from '../components/Notice'
import { Body } from '../components/Body'
import { Icon } from '../components/Icon'

export const PlatformAddPage: React.FC = () => {
  let { platform = '', redirect } = useParams<{ platform?: string; redirect?: string }>()
  const platformObj = platforms.get(platform)
  const defaultServices = platformObj.services ? platformObj.services.map(s => s.application) : [28]
  const canRegister = useSelector(selectCanRegister)
  const [platformTags, setPlatformTags] = useState<string[]>([])
  const [serviceTypes, setServiceTypes] = useState<number[]>(defaultServices)
  const [oneTimeUse, setOneTimeUse] = useState(false)
  const xs = useMediaQuery((theme: Theme) => theme.breakpoints.down('md'))
  const { t } = useTranslation()

  return (
    <Body center>
      <Stack
        flexDirection={{ xs: 'column', md: 'row' }}
        justifyContent={{ xs: 'center', md: 'left' }}
        alignItems={{ xs: 'left', md: 'top' }}
        paddingX={{ xs: 3, md: 0 }}
        paddingBottom={5}
        flexWrap="wrap"
      >
        <Stack
          maxWidth={{ md: 130 }}
          marginTop={{ xs: 0, md: 2 }}
          marginRight={{ md: 3 }}
          marginBottom={{ xs: 3, md: 0 }}
          flexDirection={{ xs: 'row', md: 'column' }}
        >
          <Icon name={platform} fontSize={100} inlineLeft={xs} platformIcon />
          {platformObj.installation?.command && canRegister && (
            <Stack alignItems={{ xs: 'flex-start', md: 'flex-end' }}>
              {!xs && <Divider sx={{ marginTop: 4, width: '80%' }} />}
              <AddPlatformServices
                types={serviceTypes}
                onChange={type => setServiceTypes(type)}
                alignItems={{ xs: 'flex-start', md: 'flex-end' }}
              />
              <AddPlatformTags
                tags={platformTags}
                onChange={tags => setPlatformTags(tags)}
                alignItems={{ xs: 'flex-start', md: 'flex-end' }}
              />
              <Stack alignItems={{ xs: 'flex-start', md: 'flex-end' }} marginTop={2} width="100%">
                <Chip
                  label={
                    oneTimeUse
                      ? t('platformAddPage.oneTimeUse', 'ONE-TIME USE')
                      : t('platformAddPage.multiUse', 'MULTI USE')
                  }
                  size="small"
                  color={oneTimeUse ? 'primary' : 'default'}
                  onClick={() => setOneTimeUse(!oneTimeUse)}
                  sx={{
                    fontWeight: 500,
                    letterSpacing: 1,
                    color: oneTimeUse ? undefined : 'grayDarker.main',
                    whiteSpace: 'nowrap',
                    width: 120,
                    '& .MuiChip-label': { width: '100%', textAlign: 'center', px: 0 },
                  }}
                />
              </Stack>
            </Stack>
          )}
        </Stack>
        <Stack
          sx={{
            alignItems: xs ? 'left' : undefined,
            borderLeft: xs ? undefined : '1px solid',
            borderLeftColor: 'grayLighter.main',
            paddingLeft: xs ? 0 : 4.5,
            paddingRight: xs ? 0 : 0,
            paddingTop: 2.25,
            paddingBottom: 2.25,
            maxWidth: 650,
            width: xs ? '100%' : 'auto',
            '& .MuiAvatar-root': { marginTop: 0.375 },
            '& > .MuiTypography-body2': { marginBottom: 0.75 },
          }}
        >
          {!canRegister ? (
            <Box>
              <Notice>
                {t(
                  'platformAddPage.managePermissionRequired',
                  'You must have the Manage permission to add a device to this organization.'
                )}
              </Notice>
            </Box>
          ) : platformObj.override ? (
            <platformObj.override platform={platformObj} tags={platformTags} serviceTypes={serviceTypes} oneTimeUse={oneTimeUse} />
          ) : platformObj.installation?.command && !platformObj.installation?.download ? (
            <AddDevice platform={platformObj} tags={platformTags} serviceTypes={serviceTypes} redirect={redirect} oneTimeUse={oneTimeUse} />
          ) : (
            <>
              <AddDownload platform={platformObj} />
              {platformObj.hasScreenView && (
                <>
                  <Typography variant="body2" color="textSecondary" paddingBottom={1}>
                    {t(
                      'platformAddPage.screenViewCodeHint',
                      'Or use this registration code to manually claim the ScreenView app.'
                    )}
                  </Typography>
                  <AddDevice
                    platform={platformObj}
                    tags={platformTags}
                    serviceTypes={serviceTypes}
                    redirect={redirect}
                    oneTimeUse={oneTimeUse}
                    minimal
                  />
                </>
              )}
            </>
          )}
        </Stack>
      </Stack>
    </Body>
  )
}
